import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useInnerHeroImage } from '../hooks/useInnerHeroImage';
import {
  INNER_HERO_SUBTITLE_CLASS,
  INNER_HERO_TITLE_CLASS,
  InnerHeroBackground,
  InnerHeroShell,
} from '../ui/InnerHeroShell';

/**
 * Amenities page hero. Keeps the `amenities-hero.*` keys the shared
 * PageHero instance saved to, so existing edits carry over.
 */

const TITLE = '<p>Amenities</p>';
const SUBTITLE = '<p>Pools, Fitness Centers, Lounges &amp; Outdoor Spaces</p>';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Amenities banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The strongest community amenity photo this property actually has — a shared indoor or outdoor amenity space, not an apartment interior. Alt text describes the chosen photo.',
    context:
      'Full-bleed inner page hero banner behind the "Amenities" title, under a dark overlay; crops to a wide landscape strip. Must be sharp, well lit and uncluttered — never a map, logo, or graphic. Use a photo not already used by the homepage hero or the other inner page heroes (Floor Plans, Gallery, Neighborhood, Virtual Tour, Contact). Reuse a photo only if no unused amenity photo fits.',
  },
};

export function AmenitiesHero() {
  const bg = useInnerHeroImage('amenities-hero.bg_image', bgImageDef, 'amenities', {
    altText: 'Amenities banner',
  });

  return (
    <InnerHeroShell
      background={
        <EditableSlotGroup
          groupPath="amenities-hero.bg_image"
          definition={bgImageDef}
          className="absolute inset-0"
        >
          <InnerHeroBackground image={bg.image} alt={bg.alt} />
        </EditableSlotGroup>
      }
    >
      <RichTextContent
        path="amenities-hero.subtitle"
        label="Subtitle"
        defaultValue={SUBTITLE}
        as="h2"
        className={INNER_HERO_SUBTITLE_CLASS}
      />
      <RichTextContent
        path="amenities-hero.title"
        label="Title"
        defaultValue={TITLE}
        as="h1"
        className={INNER_HERO_TITLE_CLASS}
      />
    </InnerHeroShell>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesHero',
  displayName: 'Amenities Hero',
  tokens: {
    'amenities-hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Pools, Fitness Centers, Lounges &amp; Outdoor Spaces</p>',
    },
    'amenities-hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Amenities</p>',
    },
  },
  slotGroups: {
    'amenities-hero.bg_image': bgImageDef,
  },
};

(AmenitiesHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesHero;
